const API_BASE = '/api';
const STORAGE_KEY = 'synex_blog_posts_v1';

const defaultPosts = [
  {
    id: 'intro-to-synex',
    slug: 'intro-to-synex',
    title: 'Introducing Synex Agency: Web + AI that Works',
    excerpt: 'Discover how Synex blends web design and AI automation to help businesses scale with less effort.',
    content: `## Welcome to Synex Agency

We build **high-converting websites** and **AI automation systems** that work together.

- Modern landing pages that capture leads
- AI chatbots that qualify and nurture automatically
- WhatsApp & Instagram automation that keeps customers engaged

> Web + AI is the easiest way to level up your business online.

Ready to get started?`,
    published: true,
    createdAt: new Date().toISOString(),
  },
  {
    id: 'ai-automation-101',
    slug: 'ai-automation-101',
    title: 'AI Automation 101: What You Need to Know',
    excerpt: 'A quick guide to choosing the right AI automation for your business.',
    content: `## What is AI Automation?

AI automation uses intelligent software to handle repetitive tasks so your team can focus on growth.

### Typical uses:

- Chatbots that respond instantly to website visitors
- WhatsApp follow-up flows that keep leads warm
- Automated booking & calendars

### Why it matters

Businesses using automation can respond 10x faster and close more leads without hiring more staff.

Want help picking the right stack? Reach out — we can map a plan in 30 minutes.`,
    published: true,
    createdAt: new Date().toISOString(),
  },
];

function getLocalPosts() {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(defaultPosts));
      return defaultPosts;
    }
    return JSON.parse(stored);
  } catch (e) {
    console.warn('Failed to read posts from localStorage', e);
    return defaultPosts;
  }
}

function setLocalPosts(posts) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(posts));
  } catch (e) {
    console.warn('Failed to save posts to localStorage', e);
  }
}

async function apiFetch(path, options = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  });

  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(errorText || `API request failed (${res.status})`);
  }

  return res.json();
}

export async function getPosts({ published = true } = {}) {
  try {
    const params = published ? '?published=true' : '';
    return await apiFetch(`/posts${params}`);
  } catch (e) {
    console.warn('API not available, falling back to local posts', e);
    const posts = getLocalPosts();
    return published ? posts.filter((p) => p.published) : posts;
  }
}

export async function getPostBySlug(slug) {
  if (!slug) return null;

  try {
    return await apiFetch(`/posts/${encodeURIComponent(slug)}`);
  } catch (e) {
    console.warn('API not available, falling back to local post fetch', e);
    return getLocalPosts().find((p) => p.slug === slug) || null;
  }
}

export async function savePost(post, adminPassword) {
  try {
    return await apiFetch('/posts', {
      method: 'POST',
      headers: {
        'x-admin-secret': adminPassword,
      },
      body: JSON.stringify(post),
    });
  } catch (e) {
    console.warn('API not available, saving post locally', e);
    const posts = getLocalPosts();
    const existingIndex = posts.findIndex((p) => p.id === post.id);
    if (existingIndex > -1) {
      posts[existingIndex] = post;
    } else {
      posts.unshift(post);
    }
    setLocalPosts(posts);
    return { success: true };
  }
}

export async function deletePost(id, adminPassword) {
  try {
    return await apiFetch(`/posts/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      headers: {
        'x-admin-secret': adminPassword,
      },
    });
  } catch (e) {
    console.warn('API not available, deleting post locally', e);
    const posts = getLocalPosts().filter((p) => p.id !== id);
    setLocalPosts(posts);
    return { success: true };
  }
}

export function slugify(value) {
  return value
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9\-]/g, '')
    .replace(/\-+/g, '-');
}
